/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
/**
 * DbCacheConfigService should be used as part of the root module to setup DB configurations and not
 * post that.
 */
import { Injectable } from '@angular/core';
import { DbService } from './db.service';
import { DbCache } from './models/db-cache.class';
import { DbCacheMap, DbCacheDefinitionMap } from './models/db.models';

@Injectable({
    providedIn: 'root'
})
export class DbCacheConfigService {
    private readonly globalLoadMap: DbCacheDefinitionMap;
    readonly userLoadMap: DbCacheDefinitionMap;
    private readonly globalCacheMap: DbCacheMap;
    private readonly userCacheMap: Map<string, DbCacheMap>;

    get globalCacheDefinitions() {
        return this.globalLoadMap;
    }

    get userCacheDefinitions() {
        return this.userLoadMap;
    }

    get globalCache() {
        return this.globalCacheMap;
    }

    get userCache() {
        return this.userCacheMap;
    }

    constructor(private dbService: DbService) {
        this.globalLoadMap = new Map();
        this.userLoadMap = new Map();
        this.globalCacheMap = new Map();
        this.userCacheMap = new Map();
    }

    loadGlobalKey(storeName: string, key: string) {
        if (!this.globalLoadMap.has(storeName)) {
            this.globalLoadMap.set(storeName, new Set());
        }
        this.globalLoadMap.get(storeName).add(key);
        if (!this.globalCacheMap.has(storeName)) {
            this.globalCacheMap.set(storeName, new Map());
        }
        const storeMap = this.globalCacheMap.get(storeName);
        storeMap.set(key, new DbCache(this.dbService.dbHandler, storeName, key));
    }

    loadUserKey(storeName: string, key: string) {
        if (!this.userLoadMap.has(storeName)) {
            this.userLoadMap.set(storeName, new Set());
        }
        this.userLoadMap.get(storeName).add(key);
    }
}
